// useDoctrinePicks — client-side hook that loads doctrine fallback picks
// from the substrate view when a product has no T1 edge-backed picks.
//
// Reads vw_substrate_fixtures (same view the server-side selectors use),
// runs the two doctrine selectors in-memory, and returns shaped picks
// ready for the DoctrineSection component.
//
// Trust contract:
//   - Read-only via RLS-scoped SELECT on the substrate view.
//   - No service-role, no RPC, no LLM calls.
//   - Skips fetch entirely when disabled (t1HasPicks = true).

import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import type { DoctrinePick as UIPick } from "../components/DoctrineSection.tsx";
import { useSubstrateStatus, type SubstrateWeekStatus } from "./useSubstrateStatus.ts";

interface SubstrateRow {
  fixture_id: string;
  fixture_uuid: string;
  home_team: string | null;
  away_team: string | null;
  competition: string | null;
  sport: string | null;
  kickoff_at: string;
  market: string;
  selection: string;
  fair_prob: number;
  fair_odds: number;
  market_prob: number | null;
  market_odds: number | null;
  clv_gap: number | null;
  conviction_tier: string | null;
  desk_confidence: number | null;
  desk_ev: number | null;
  intel_count: number | null;
  priced_at: string;
}

export interface UseDoctrinPicksOpts {
  /** When true, skip fetching (product already has T1 picks). */
  t1HasPicks?: boolean;
  /** Fixture IDs already shown as T1 picks — exclude from doctrines. */
  excludeFixtureIds?: string[];
  /** Max picks per doctrine (default 5). */
  maxPicks?: number;
  /** Sport filter (default "football"). */
  sport?: string;
  /** Hours ahead window (default 168 = 7 days). */
  hoursAhead?: number;
  substrateView?: "vw_substrate_fixtures" | "vw_owl_substrate_fixtures";
  fixtureWindow?: { start: string; end: string };
}

export interface UseDoctrinPicksResult {
  probabilityPicks: UIPick[];
  valuePicks: UIPick[];
  loading: boolean;
  error: string | null;
  /** True when the substrate itself has no priced fixtures (absolute-zero). */
  substrateEmpty: boolean;
  /** Substrate week metadata for the absolute-zero state display. */
  substrateWeek: SubstrateWeekStatus | null;
}

const COLUMNS =
  "fixture_id,fixture_uuid,home_team,away_team,competition,sport,kickoff_at," +
  "market,selection,fair_prob,fair_odds,market_prob,market_odds,clv_gap," +
  "conviction_tier,desk_confidence,desk_ev,intel_count,priced_at";

function humanize(r: SubstrateRow): { market: string; selection: string } {
  const home = r.home_team ?? "Home";
  const away = r.away_team ?? "Away";
  const m = r.market;
  const sel = r.selection;

  if (m === "match_winner" || m === "first_half_winner" || m === "second_half_winner") {
    const prefix = m === "first_half_winner" ? "1H " : m === "second_half_winner" ? "2H " : "";
    const label = `${prefix}Match Result`;
    if (sel === "home") return { market: label, selection: home };
    if (sel === "away") return { market: label, selection: away };
    return { market: label, selection: "Draw" };
  }
  if (m === "ou") return { market: "Total Goals", selection: sel === "over" ? "Over 2.5" : "Under 2.5" };

  const halfTotals = m.match(/^(first|second)_half_total_goals_(\d+_\d+)$/);
  if (halfTotals) {
    const half = halfTotals[1] === "first" ? "1H" : "2H";
    const line = (halfTotals[2] ?? "").replace("_", ".");
    return { market: `${half} Total Goals`, selection: `${sel === "over" ? "Over" : "Under"} ${line}` };
  }

  const totalGoals = m.match(/^total_goals_(?:over|under)_(\d+_\d+)$/);
  if (totalGoals) {
    const line = (totalGoals[1] ?? "").replace("_", ".");
    return { market: "Total Goals", selection: `${sel === "over" || m.includes("over") ? "Over" : "Under"} ${line}` };
  }

  if (m === "btts") return { market: "Both Teams to Score", selection: sel === "yes" ? "Yes" : "No" };
  if (m === "btts_yes") return { market: "Both Teams to Score", selection: "Yes" };
  if (m === "btts_no") return { market: "Both Teams to Score", selection: "No" };

  if (m === "first_half_dc" || m === "second_half_dc") {
    const half = m === "first_half_dc" ? "1H" : "2H";
    const selLabel = sel === "home_or_draw" ? `${home} or Draw` : sel === "home_or_away" ? `${home} or ${away}` : sel === "away_or_draw" ? `${away} or Draw` : sel;
    return { market: `${half} Double Chance`, selection: selLabel };
  }

  const teamGoals = m.match(/^team_(?:goals|total)_(home|away)_(?:over|under)_(\d+_\d+)$/) || m.match(/^team_goals_(home|away)_(\d+_\d+)$/);
  if (teamGoals) {
    const team = teamGoals[1] === "home" ? home : away;
    const line = (teamGoals[2] ?? "").replace("_", ".");
    const direction = m.includes("over") ? "Over" : m.includes("under") ? "Under" : (sel === "over" ? "Over" : "Under");
    return { market: `${team} Goals`, selection: `${direction} ${line}` };
  }

  const corners = m.match(/^total_corners_(\d+_\d+)$/);
  if (corners) {
    const line = (corners[1] ?? "").replace("_", ".");
    return { market: "Total Corners", selection: `${sel === "over" ? "Over" : "Under"} ${line}` };
  }

  if (m === "both_halves_1_5") return { market: "Both Halves Goals", selection: sel === "over" ? "Both Halves Over 1.5" : "Both Halves Under 1.5" };
  if (m === "goal_bounds") {
    const buckets: Record<string, string> = { "0_1": "0-1 Goals", "2_3": "2-3 Goals", "4_plus": "4+ Goals" };
    return { market: "Total Goals (Range)", selection: buckets[sel] ?? sel };
  }

  if (m.startsWith("asian_handicap")) {
    const side = sel.startsWith("home") ? home : sel.startsWith("away") ? away : sel.split("_")[0] ?? sel;
    const lineMatch = sel.match(/(\d+)_(\d+)$/);
    const line = lineMatch ? `${lineMatch[1]}.${lineMatch[2]}` : "";
    const sign = sel.includes("plus") ? "+" : sel.includes("minus") ? "-" : "";
    return { market: "Asian Handicap", selection: `${side} ${sign}${line}`.trim() };
  }

  return { market: m.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()), selection: sel.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) };
}

function offeredOdds(r: SubstrateRow): number {
  return r.market_odds ?? r.fair_odds;
}

function formatKickoff(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function toUIPick(r: SubstrateRow, doctrine: "probability" | "value"): UIPick {
  const h = humanize(r);
  const offered = offeredOdds(r);
  const ratio = r.fair_odds > 1 ? offered / r.fair_odds : 1;

  const reasoning = doctrine === "probability"
    ? r.fair_prob >= 0.65
      ? `${h.selection} is the clear statistical favourite in this fixture.`
      : r.fair_prob >= 0.50
        ? `The numbers lean toward ${h.selection} in this fixture.`
        : `${h.selection} holds a slight edge in the probability model.`
    : ratio >= 1.10
      ? `${h.selection} pays generously relative to the assessed probability.`
      : ratio >= 1.02
        ? `The offered odds on ${h.selection} are slightly above fair value.`
        : `${h.selection} offers the best odds-to-probability ratio in this fixture.`;

  return {
    id: `${doctrine}-${r.fixture_id}-${r.market}-${r.selection}`,
    doctrine,
    fixture: `${r.home_team ?? "Home"} vs ${r.away_team ?? "Away"}`,
    league: r.competition ?? "",
    kickoffDisplay: formatKickoff(r.kickoff_at),
    market: h.market,
    selection: h.selection,
    offeredOdds: Number(offered.toFixed(2)),
    fairProb: Number(r.fair_prob),
    payoutRatio: Number(ratio.toFixed(3)),
    reasoning,
  };
}

function bestPerFixture(
  rows: SubstrateRow[],
  scoreFn: (r: SubstrateRow) => number,
): SubstrateRow[] {
  const best = new Map<string, SubstrateRow>();
  for (const r of rows) {
    const cur = best.get(r.fixture_id);
    if (!cur || scoreFn(r) > scoreFn(cur)) best.set(r.fixture_id, r);
  }
  return [...best.values()].sort((a, b) => scoreFn(b) - scoreFn(a));
}

export function useDoctrinePicks(opts: UseDoctrinPicksOpts = {}): UseDoctrinPicksResult {
  const {
    t1HasPicks = false,
    excludeFixtureIds = [],
    maxPicks = 5,
    sport = "football",
    hoursAhead = 168,
    substrateView = "vw_substrate_fixtures",
    fixtureWindow,
  } = opts;
  const windowStart = fixtureWindow?.start;
  const windowEnd = fixtureWindow?.end;

  const [rows, setRows] = useState<SubstrateRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const excludeSet = useMemo(() => new Set(excludeFixtureIds), [excludeFixtureIds]);

  const fetch = useCallback(async () => {
    if (t1HasPicks) return;
    setLoading(true);
    setError(null);
    try {
      const client = getBrowserClient();
      const horizon = new Date(Date.now() + hoursAhead * 3600_000).toISOString();
      let query = (client as any)
        .from(substrateView)
        .select(COLUMNS)
        .eq("sport", sport);
      if (windowStart && windowEnd) {
        const futureStart = new Date(Math.max(Date.parse(windowStart), Date.now())).toISOString();
        query = query.gte("kickoff_at", futureStart).lt("kickoff_at", windowEnd);
      } else {
        query = query.lte("kickoff_at", horizon);
      }
      const { data, error: err } = await query;

      if (err) throw new Error(err.message);
      setRows((data ?? []) as SubstrateRow[]);
    } catch (e: any) {
      setError(e.message ?? "Failed to load doctrine picks");
    } finally {
      setLoading(false);
    }
  }, [t1HasPicks, sport, hoursAhead, substrateView, windowStart, windowEnd]);

  useEffect(() => { fetch(); }, [fetch]);

  const { probabilityPicks, valuePicks } = useMemo(() => {
    if (t1HasPicks || rows.length === 0) {
      return { probabilityPicks: [] as UIPick[], valuePicks: [] as UIPick[] };
    }

    const pool = rows.filter((r) => {
      if (excludeSet.has(r.fixture_id)) return false;
      const prob = Number(r.fair_prob);
      if (prob < 0.30 || prob > 0.85) return false;
      const odds = offeredOdds(r);
      if (odds < 1.25 || odds > 15.0) return false;
      return true;
    });

    const probRanked = bestPerFixture(pool, (r) => Number(r.fair_prob));
    const probPicks = probRanked.slice(0, maxPicks).map((r) => toUIPick(r, "probability"));

    const probFixtures = new Set(probRanked.slice(0, maxPicks).map((r) => r.fixture_id));
    const valuePool = pool.filter((r) => !probFixtures.has(r.fixture_id));
    const valueRanked = bestPerFixture(valuePool, (r) => {
      const fo = r.fair_odds > 1 ? r.fair_odds : 1;
      return (r.market_odds ?? r.fair_odds) / fo;
    });
    const valPicks = valueRanked.slice(0, maxPicks).map((r) => toUIPick(r, "value"));

    return { probabilityPicks: probPicks, valuePicks: valPicks };
  }, [rows, t1HasPicks, excludeSet, maxPicks]);

  const substrateStatus = useSubstrateStatus();
  const substrateEmpty = !loading && !t1HasPicks && rows.length === 0 && !error;

  return {
    probabilityPicks,
    valuePicks,
    loading: loading || substrateStatus.loading,
    error,
    substrateEmpty,
    substrateWeek: substrateStatus.currentWeek,
  };
}
